import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import { CalendarCheck, CreditCard, TrendingUp, Megaphone, ChevronRight, ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = 'https://customer-assets.emergentagent.com/job_womens-wellness-72/artifacts/dyehgy3v_Logo.jpg';

const MOTIVATIONAL = [
  "Every step forward is progress.",
  "Strong body, strong mind — you're doing amazing!",
  "Consistency is the key to transformation.",
  "Your only competition is who you were yesterday.",
  "Small steps lead to big changes.",
  "Progress, not perfection.",
  "Show up today. Your future self will thank you.",
];

function getMotivation() {
  const d = new Date();
  return MOTIVATIONAL[d.getDay() % MOTIVATIONAL.length];
}

function getTimeGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = (Date.now() - new Date(isoStr)) / 1000;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function WeightChangeBadge({ change }) {
  if (change === null || change === undefined) return <span className="text-xs text-[#A8A29E] font-body">— kg</span>;
  if (change === 0) return (
    <span className="flex items-center gap-1 text-sm font-body text-[#57534E]">
      <Minus className="w-3.5 h-3.5" /> 0 kg
    </span>
  );
  return (
    <span className={`flex items-center gap-1 text-sm font-body font-semibold ${change < 0 ? 'text-green-600' : 'text-orange-500'}`}>
      {change < 0 ? <ArrowDown className="w-3.5 h-3.5" strokeWidth={2} /> : <ArrowUp className="w-3.5 h-3.5" strokeWidth={2} />}
      {Math.abs(change)} kg
    </span>
  );
}

const SECTIONS = [
  { label: 'My Attendance', icon: CalendarCheck, path: '/student/attendance', color: 'bg-green-50 text-green-600' },
  { label: 'My Payments', icon: CreditCard, path: '/student/payments', color: 'bg-orange-50 text-orange-600' },
  { label: 'My Progress', icon: TrendingUp, path: '/student/progress', color: 'bg-amber-50 text-amber-600' },
  { label: 'Announcements', icon: Megaphone, path: '/student/announcements', color: 'bg-rose-50 text-rose-600' },
];

export default function StudentHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly'); // 'monthly' | 'yearly'

  useEffect(() => {
    axios.get(`${API}/api/student/dashboard`, { headers: getAuthHeaders() })
      .then(r => setDash(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const name = user?.name || 'there';
  const weightChange = period === 'monthly' ? dash?.weight_change_monthly : dash?.weight_change_yearly;
  const attendance = period === 'monthly' ? dash?.monthly_attendance : dash?.yearly_attendance;
  const attPct = attendance?.total > 0 ? Math.round((attendance.present / attendance.total) * 100) : null;

  const nextDue = dash?.next_due;
  const daysUntilDue = nextDue ? Math.round((new Date(nextDue + 'T00:00:00') - new Date(new Date().toDateString())) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div className="space-y-5 animate-fade-in-up">

      {/* Greeting banner */}
      <div className="bg-gradient-to-br from-[#F59E0B] to-[#D97706] rounded-3xl p-5 sm:p-7 text-white relative overflow-hidden shadow-button">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-8 w-20 h-20 bg-white/10 rounded-full translate-y-1/2" />

        <div className="relative z-10 flex items-start justify-between gap-4">
          <div>
            <p className="text-white/70 text-xs font-body mb-0.5">{getTimeGreeting()}</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold leading-tight">Hi, {name}! 👋</h1>
            {dash?.latest_weight && (
              <div className="flex items-center gap-3 mt-2">
                <span className="text-white/90 text-sm font-body">
                  {dash.latest_weight} kg
                  {dash.goal_weight && (
                    <span className="text-white/60 ml-1">/ {dash.goal_weight} kg goal</span>
                  )}
                </span>
              </div>
            )}
            <p className="text-white/70 text-xs font-body mt-3 italic max-w-xs">"{getMotivation()}"</p>
          </div>
          <img src="https://customer-assets.emergentagent.com/job_womens-wellness-72/artifacts/mi9gwt69_image.png" alt="logo" className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-2xl shrink-0" />
        </div>
      </div>

      {/* Period toggle */}
      <div className="flex items-center gap-2 bg-white rounded-2xl p-1.5 shadow-card border border-[#E7E5E4]/50 w-fit">
        <button onClick={() => setPeriod('monthly')}
          className={`px-4 py-2 rounded-xl text-xs font-heading font-medium transition-all ${period === 'monthly' ? 'bg-[#F59E0B] text-white' : 'text-[#57534E] hover:text-[#D97706]'}`}>
          This Month
        </button>
        <button onClick={() => setPeriod('yearly')}
          className={`px-4 py-2 rounded-xl text-xs font-heading font-medium transition-all ${period === 'yearly' ? 'bg-[#F59E0B] text-white' : 'text-[#57534E] hover:text-[#D97706]'}`}>
          This Year
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Weight change */}
        <div onClick={() => navigate('/student/progress')}
          className="bg-white rounded-2xl p-4 shadow-card border border-[#E7E5E4]/50 cursor-pointer hover:shadow-card-hover transition-all">
          <p className="text-xs text-[#A8A29E] font-body mb-1">Weight Change</p>
          {loading ? <div className="h-5 w-12 bg-gray-100 rounded animate-pulse" /> : <WeightChangeBadge change={weightChange} />}
          <p className="text-xs text-[#A8A29E] font-body mt-0.5">{period === 'monthly' ? 'vs 30 days ago' : 'vs last year'}</p>
        </div>

        {/* Attendance */}
        <div onClick={() => navigate('/student/attendance')}
          className="bg-white rounded-2xl p-4 shadow-card border border-[#E7E5E4]/50 cursor-pointer hover:shadow-card-hover transition-all">
          <p className="text-xs text-[#A8A29E] font-body mb-1">Attendance</p>
          {loading ? <div className="h-5 w-12 bg-gray-100 rounded animate-pulse" /> : (
            <p className={`text-sm font-heading font-semibold ${attPct !== null ? (attPct >= 75 ? 'text-green-600' : 'text-orange-500') : 'text-[#A8A29E]'}`}>
              {attPct !== null ? `${attPct}%` : '—'}
            </p>
          )}
          <p className="text-xs text-[#A8A29E] font-body mt-0.5">{attendance?.total > 0 ? `${attendance.present}/${attendance.total} classes` : 'No data'}</p>
        </div>

        {/* Fee due */}
        <div onClick={() => navigate('/student/payments')}
          className={`bg-white rounded-2xl p-4 shadow-card border cursor-pointer hover:shadow-card-hover transition-all
            ${daysUntilDue !== null && daysUntilDue < 0 ? 'border-red-200 bg-red-50/30' : daysUntilDue !== null && daysUntilDue <= 7 ? 'border-orange-200 bg-orange-50/20' : 'border-[#E7E5E4]/50'}`}>
          <p className="text-xs text-[#A8A29E] font-body mb-1">Fee Due</p>
          {loading ? <div className="h-5 w-16 bg-gray-100 rounded animate-pulse" /> : (
            <p className={`text-sm font-heading font-semibold ${daysUntilDue !== null && daysUntilDue < 0 ? 'text-red-600' : daysUntilDue !== null && daysUntilDue <= 7 ? 'text-orange-500' : 'text-[#292524]'}`}>
              {nextDue ? fmtDate(nextDue) : '—'}
            </p>
          )}
          {daysUntilDue !== null && <p className="text-xs text-[#A8A29E] font-body mt-0.5">{daysUntilDue < 0 ? 'Overdue' : daysUntilDue === 0 ? 'Due today' : `In ${daysUntilDue} days`}</p>}
        </div>

        {/* Progress */}
        <div onClick={() => navigate('/student/progress')}
          className="bg-white rounded-2xl p-4 shadow-card border border-[#E7E5E4]/50 cursor-pointer hover:shadow-card-hover transition-all">
          <p className="text-xs text-[#A8A29E] font-body mb-1">Current Weight</p>
          {loading ? <div className="h-5 w-12 bg-gray-100 rounded animate-pulse" /> : (
            <p className="text-sm font-heading font-semibold text-[#F59E0B]">{dash?.latest_weight ? `${dash.latest_weight} kg` : '—'}</p>
          )}
          {dash?.goal_weight && <p className="text-xs text-[#A8A29E] font-body mt-0.5">Goal: {dash.goal_weight} kg</p>}
        </div>
      </div>

      {/* Latest announcements */}
      {!loading && dash?.latest_announcements?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-sm font-semibold text-[#292524]">Latest Updates</h2>
            <button onClick={() => navigate('/student/announcements')} className="text-xs text-[#F59E0B] hover:underline font-body">View all</button>
          </div>
          <div className="space-y-2">
            {dash.latest_announcements.map((ann, i) => (
              <div key={ann.id || i} data-testid={`home-ann-${i}`}
                className="bg-white rounded-2xl px-4 py-3.5 shadow-card border border-[#E7E5E4]/50 flex items-start gap-3">
                <div className="w-7 h-7 bg-[#FFEDD5] rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                  <Megaphone className="w-3.5 h-3.5 text-[#F59E0B]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-medium text-[#292524] text-xs">{ann.title}</p>
                  <p className="text-xs text-[#78716C] font-body mt-0.5 line-clamp-1">{ann.message}</p>
                </div>
                <span className="text-xs text-[#A8A29E] font-body shrink-0">{timeAgo(ann.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Sections */}
      <div>
        <h2 className="font-heading text-sm font-semibold text-[#292524] mb-3">Quick Access</h2>
        <div className="grid grid-cols-2 gap-3">
          {SECTIONS.map(card => (
            <button key={card.label} onClick={() => navigate(card.path)}
              data-testid={`student-section-${card.label.replace(/\s+/g, '-').toLowerCase()}`}
              className="bg-white rounded-2xl p-4 shadow-card border border-[#E7E5E4]/50 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300 text-left flex items-center gap-3 group">
              <div className={`p-2.5 rounded-xl shrink-0 ${card.color}`}>
                <card.icon className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-medium text-[#292524] text-xs group-hover:text-[#D97706] transition-colors">{card.label}</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-[#A8A29E] group-hover:text-[#F59E0B] transition-colors shrink-0" strokeWidth={1.5} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
